import React, { useState, useMemo } from 'react';
import { useContent } from '../../hooks/useContent';
import styles from './Guidelines.module.css';

const SECTION_IDS = ['purpose', 'principles', 'criteria', 'format', 'recommendations'];

const DEFAULT_SECTIONS = {
  purpose: {
    title: 'Purpose of Review:',
    content: `The purpose of the review is to provide an objective and constructive assessment of the scientific work submitted for consideration, to aid in the decision regarding its publication.`,
  },
  principles: {
    title: 'Guiding Principles:',
    content: `• Objectivity: Evaluate the article based on its scientific content, disregarding personal biases or preferences.
• Constructiveness: Provide clear and substantiated comments that will help authors improve their work.
• Confidentiality: Maintain the confidentiality of the review process, not disclosing information about the article or authors to third parties.
• Timeliness: Strive to provide your review within the established deadlines.`,
  },
  criteria: {
    title: 'Evaluation Criteria:',
    content: `Relevance and Novelty:
   • Does the topic align with current scientific trends?
   • Does the article offer a novel contribution to the relevant field of knowledge?

Scientific Significance:
   • Are the research results significant and well-supported?
   • Does the article have practical implications?

Methodology:
   • Is the research methodology sound and justified?
   • Is the methodology described in sufficient detail to allow for reproducibility?

Structure and Clarity:
   • Is the article logically structured and coherent?
   • Are the ideas presented clearly and understandably?
   • Does the article adhere to formatting guidelines?

Literature Review:
   • Is the literature review comprehensive and up-to-date?
   • Are citations and references formatted correctly?

Conclusions:
   • Are the conclusions supported by the research results?
   • Are the conclusions well-reasoned and clear?`,
  },
  format: {
    title: 'Review Format:',
    content: `We recommend using the following review format:
   • Summary of the Article: Briefly describe the main objectives and findings of the research.
   • Overall Evaluation: Express your general impression of the article.
   • Detailed Comments: Provide detailed comments on each of the evaluation criteria.
   • Recommendations: Formulate recommendations for improving the article.
   • Decision: Make one of the following decisions:
   • Accept without revisions
   • Accept with minor revisions
   • Accept with major revisions
   • Reject`,
  },
  recommendations: {
    title: 'Additional Recommendations:',
    content: `   • Pay attention to grammatical and stylistic errors.
   • Verify the correctness of citations and references.
   • Point out any potential contradictions or inaccuracies in the article.`,
  },
};

const Guidelines = () => {
  const { t } = useContent();
  const [openSections, setOpenSections] = useState({});

  const toggleSection = (section) => {
    setOpenSections((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  const sections = useMemo(
    () =>
      SECTION_IDS.map((id) => {
        const d = DEFAULT_SECTIONS[id] || { title: '', content: '' };
        return {
          id,
          title: t(`guidelinesReviewers.sections.${id}.title`, d.title),
          content: t(`guidelinesReviewers.sections.${id}.content`, d.content),
        };
      }),
    [t],
  );

  return (
    <div className={styles.container}>
      <div className={styles.guidelinesHeader}>
        <h1>{t('guidelinesReviewers.pageTitle', 'Guidelines for Reviewers')}</h1>
        <div className={styles.introText}>
          <p>{t('guidelinesReviewers.intro.greeting', 'Dear Reviewer,')}</p>
          <p>
            {t(
              'guidelinesReviewers.intro.body',
              'Thank you for agreeing to participate in the peer-review process for our journal. Your expertise and objective evaluation are crucial in ensuring the high quality of our published materials.',
            )}
          </p>
        </div>
      </div>

      <div className={styles.sectionsContainer}>
        {sections.map((section) => (
          <div key={section.id} className={styles.sectionItem}>
            <button
              type="button"
              className={`${styles.sectionHeader} ${openSections[section.id] ? styles.open : ''}`}
              onClick={() => toggleSection(section.id)}
            >
              <span>{section.title}</span>
              <svg
                className={`${styles.chevron} ${openSections[section.id] ? styles.rotated : ''}`}
                width="20"
                height="20"
                viewBox="0 0 20 20"
                fill="none"
              >
                <path
                  d="M5 7.5L10 12.5L15 7.5"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
            {openSections[section.id] && (
              <div className={styles.sectionContent}>
                <div className={styles.contentText}>
                  {section.content
                    .trim()
                    .split('\n')
                    .filter(Boolean)
                    .map((line, i) => (
                      <p key={i}>{line.trim()}</p>
                    ))}
                </div>
              </div>
            )}
          </div>
        ))}
      </div>

      <div className={styles.footerText}>
        <p>
          {t(
            'guidelinesReviewers.closing.text',
            'Thank you for your valuable contribution to the advancement of science!',
          )}
        </p>
      </div>
    </div>
  );
};

export default Guidelines;
